import React, { useState, useRef, useCallback, useEffect } from "react";
import { t as translate } from "../i18n/translations.js";

const API_BASE = "/api";

const BR_DEFAULTS = {
  primaryColor: "#6366f1", secondaryColor: "#8b5cf6",
  borderRadius: "16", buttonStyle: "gradient",
};
function br(branding) { return { ...BR_DEFAULTS, ...branding }; }

function useQuery() {
  return new URLSearchParams(window.location.search);
}

function buttonStyles(b) {
  const radius = Math.min(parseInt(b.borderRadius, 10) || 16, 28);
  return {
    display: "block",
    width: "100%",
    padding: "14px 20px",
    fontSize: 16,
    fontWeight: 700,
    color: "#fff",
    border: "none",
    borderRadius: radius,
    cursor: "pointer",
    background: b.buttonStyle === "gradient"
      ? `linear-gradient(135deg, ${b.primaryColor}, ${b.secondaryColor})`
      : b.primaryColor,
    boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
  };
}

const styles = {
  page: {
    maxWidth: 640,
    margin: "0 auto",
    padding: "16px",
    minHeight: "100dvh",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: "#1a1a1a",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  },
  header: { textAlign: "center", padding: "8px 0 12px" },
  title: { fontSize: 24, fontWeight: 800, margin: 0, lineHeight: 1.25 },
  subtitle: { fontSize: 14, color: "#666", margin: "6px 0 0", lineHeight: 1.4 },
  toggleRow: {
    display: "flex",
    justifyContent: "center",
    gap: 8,
    margin: "12px 0",
  },
  toggleBtn: (active) => ({
    padding: "8px 18px",
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 999,
    border: active ? "2px solid #1a1a1a" : "1px solid #ddd",
    background: active ? "#1a1a1a" : "#fff",
    color: active ? "#fff" : "#444",
    cursor: "pointer",
    minHeight: 40,
  }),
  carousel: {
    display: "flex",
    overflowX: "auto",
    scrollSnapType: "x mandatory",
    WebkitOverflowScrolling: "touch",
    gap: 12,
    margin: "0 -16px",
    padding: "4px 16px 8px",
    scrollbarWidth: "none",
  },
  slide: {
    flex: "0 0 100%",
    scrollSnapAlign: "center",
    boxSizing: "border-box",
  },
  card: {
    background: "#fff",
    borderRadius: 20,
    padding: 16,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    height: "100%",
    boxSizing: "border-box",
  },
  cardImgWrap: {
    background: "#f7f7f9",
    borderRadius: 14,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 260,
    maxHeight: "48dvh",
  },
  cardImg: {
    width: "100%",
    height: "100%",
    maxHeight: "48dvh",
    objectFit: "contain",
    display: "block",
  },
  cardTitle: {
    fontSize: 17,
    fontWeight: 700,
    margin: 0,
    lineHeight: 1.35,
    textAlign: "center",
    minHeight: 46,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  counterRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    margin: "10px 0 4px",
  },
  arrowBtn: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    border: "1px solid #ddd",
    background: "#fff",
    fontSize: 20,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#333",
  },
  counter: { fontSize: 14, fontWeight: 600, color: "#555", minWidth: 60, textAlign: "center" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: 12,
  },
  gridCard: {
    background: "#fff",
    borderRadius: 14,
    padding: 10,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    cursor: "pointer",
    border: "none",
    textAlign: "center",
    fontFamily: "inherit",
  },
  gridImg: {
    width: "100%",
    height: 160,
    objectFit: "contain",
    background: "#f7f7f9",
    borderRadius: 10,
    display: "block",
  },
  gridTitle: { fontSize: 13, fontWeight: 600, lineHeight: 1.3, color: "#1a1a1a", margin: 0 },
  message: {
    textAlign: "center",
    padding: "48px 24px",
    color: "#666",
    fontSize: 15,
    lineHeight: 1.5,
  },
  swipeHintDots: {
    display: "flex",
    justifyContent: "center",
    gap: 6,
    marginTop: 8,
  },
};

export default function ShowroomPage() {
  const query = useQuery();
  const shop = query.get("shop") || "";
  const hostname = query.get("hostname") || "";

  const [lang, setLang] = useState("nl");
  const [branding, setBranding] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState("swipe"); // swipe | grid
  const [current, setCurrent] = useState(0);
  const carouselRef = useRef(null);

  useEffect(() => {
    if (!shop) {
      setError(translate(lang, "showroom.invalidLink"));
      setLoading(false);
      return;
    }
    const params = new URLSearchParams({ shop });
    if (hostname) params.set("hostname", hostname);
    fetch(`${API_BASE}/widget/showroom?${params.toString()}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error && !data.products?.length) {
          setError(translate(lang, "showroom.loadFailed"));
          return;
        }
        setProducts(data.products || []);
        if (data.branding) setBranding(data.branding);
        if (data.language) setLang(data.language);
      })
      .catch(() => setError(translate(lang, "showroom.loadFailed")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop, hostname]);

  const openTryOn = useCallback((product) => {
    const url = `/try-on?shop=${encodeURIComponent(shop)}` +
      `&product=${encodeURIComponent(product.productId)}` +
      `&title=${encodeURIComponent(product.title || "")}`;
    window.location.href = url;
  }, [shop]);

  // Track current slide from scroll position
  const onCarouselScroll = useCallback(() => {
    const el = carouselRef.current;
    if (!el || !el.children.length) return;
    const slideWidth = el.children[0].offsetWidth + 12; // + gap
    const idx = Math.round(el.scrollLeft / slideWidth);
    setCurrent(Math.max(0, Math.min(idx, products.length - 1)));
  }, [products.length]);

  const scrollTo = useCallback((idx) => {
    const el = carouselRef.current;
    if (!el || !el.children.length) return;
    const clamped = Math.max(0, Math.min(idx, products.length - 1));
    const slideWidth = el.children[0].offsetWidth + 12;
    el.scrollTo({ left: clamped * slideWidth, behavior: "smooth" });
  }, [products.length]);

  const b = br(branding);
  const btnStyle = buttonStyles(b);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.message}>{translate(lang, "tryOn.loading")}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <header style={styles.header}>
          <h1 style={styles.title}>{translate(lang, "showroom.title")}</h1>
        </header>
        <div style={styles.message}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.title}>{translate(lang, "showroom.title")}</h1>
        <p style={styles.subtitle}>{translate(lang, "showroom.subtitle")}</p>
      </header>

      {products.length === 0 ? (
        <div style={styles.message}>{translate(lang, "showroom.empty")}</div>
      ) : (
        <>
          <div style={styles.toggleRow}>
            <button style={styles.toggleBtn(view === "swipe")} onClick={() => setView("swipe")}>
              {translate(lang, "showroom.viewSwipe")}
            </button>
            <button style={styles.toggleBtn(view === "grid")} onClick={() => setView("grid")}>
              {translate(lang, "showroom.viewGrid")}
            </button>
          </div>

          {view === "swipe" ? (
            <>
              <div ref={carouselRef} style={styles.carousel} onScroll={onCarouselScroll}>
                {products.map((p) => (
                  <div key={p.productId} style={styles.slide}>
                    <div style={styles.card}>
                      <div style={styles.cardImgWrap}>
                        {p.imageUrl ? (
                          <img src={p.imageUrl} alt={p.title} style={styles.cardImg} loading="lazy" />
                        ) : null}
                      </div>
                      <h2 style={styles.cardTitle}>{p.title}</h2>
                      <button style={btnStyle} onClick={() => openTryOn(p)}>
                        {translate(lang, "showroom.tryThis")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div style={styles.counterRow}>
                <button
                  style={{ ...styles.arrowBtn, opacity: current === 0 ? 0.35 : 1 }}
                  onClick={() => scrollTo(current - 1)}
                  disabled={current === 0}
                  aria-label="previous"
                >
                  ‹
                </button>
                <span style={styles.counter}>{current + 1} / {products.length}</span>
                <button
                  style={{ ...styles.arrowBtn, opacity: current >= products.length - 1 ? 0.35 : 1 }}
                  onClick={() => scrollTo(current + 1)}
                  disabled={current >= products.length - 1}
                  aria-label="next"
                >
                  ›
                </button>
              </div>
            </>
          ) : (
            <div style={styles.grid}>
              {products.map((p) => (
                <button key={p.productId} style={styles.gridCard} onClick={() => openTryOn(p)}>
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt={p.title} style={styles.gridImg} loading="lazy" />
                  ) : (
                    <div style={styles.gridImg} />
                  )}
                  <p style={styles.gridTitle}>{p.title}</p>
                  <span style={{ ...btnStyle, padding: "10px 12px", fontSize: 13 }}>
                    {translate(lang, "showroom.tryThis")}
                  </span>
                </button>
              ))}
            </div>
          )}

          <p style={{ ...styles.subtitle, textAlign: "center", marginTop: 12 }}>
            {translate(lang, "showroom.itemCount", { count: products.length })}
          </p>
        </>
      )}
    </div>
  );
}
